// src/App.tsx
import { useContext, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Login } from "pages/Login";
import ProtectedRoute from "app/componentes/Protected";
import SignUpWizard from "pages/auth/SignUpWizard";
import UpsWizard from "pages/remessa/UpsWizard";
import { PedidosMinimalUpload } from "pages/pedidos/pedidosMinimalUpload";
import { HomeCaixa } from "pages/caixas/HomeCaixa";
import { EditarCaixa } from "pages/caixas/EditarCaixa";
import { CadastrarCaixa } from "pages/caixas/CadastrarCaixa";
import { paths, adminPaths } from "app/paths";
import { ehRotaPublica } from "app/rotasPublicas";
import Dashboard from "pages/dashboard";
import Sobre from "pages/sobre";
import Logout from "pages/Logout";
import WhatsAppButton from "components/WhatsAppButton";
import { AuthContext } from "context/AuthContext";
import PrivacyPolicy from "pages/privacy-policy";
import { getShopifyAppBridge } from "utils/shopifyAppBridge";
import ShopifyHome from "pages/shopify-home";
import { ShopifyInstall } from "pages/ShopifyInstall";
import { NuvemshopConectado } from "pages/nuvemshop/NuvemshopConectado";
import { NuvemshopConectar } from "pages/nuvemshop/NuvemshopConectar";
import { NuvemshopEmbedded } from "pages/nuvemshop/NuvemshopEmbedded";
import AdminLogin from "pages/admin/AdminLogin";
import AdminProtectedRoute from "app/componentes/AdminProtectedRoute";
import AdminDashboard from "pages/admin/dashboard";
import AdminClientes from "pages/admin/clientes";
import AdminClienteDetalhe from "pages/admin/clientes/ClienteDetalhe";
import AdminEnvios from "pages/admin/envios";
import AdminIntegracoes from "pages/admin/integracoes";
import AdminComunicacao from "pages/admin/comunicacao";
import AdminChangelog from "pages/admin/changelog";
import AdminRoadmap from "pages/admin/roadmap";
import AdminEquipe from "pages/admin/equipe";
import AdminLogs from "pages/admin/logs";
import AdminRelatorios from "pages/admin/relatorios";
import AdminSalaOperacoes from "pages/admin/sala-operacoes";
import AdminMaquinaDoTempo from "pages/admin/maquina-do-tempo";
import AdminChamados from "pages/admin/chamados";
import AdminChamadoDetalhe from "pages/admin/chamados/ChamadoDetalhe";
import AdminCarteira from "pages/admin/carteira";
import AdminRegimes from "pages/admin/regimes";
import StatusPage from "pages/status";
import ApiDocsPage from "pages/api-docs";

function App() {
  const location = useLocation();
  const auth = useContext(AuthContext);

  const params = new URLSearchParams(location.search);
  const isEmbeddedShopify =
    !!params.get("host") || params.get("embedded") === "1";
  const hmac = params.get("hmac");

  useEffect(() => {
    if (isEmbeddedShopify && hmac) {
      const apiBase = String(
        (import.meta as any).env?.VITE_API_BASE_URL || "https://back-exporta.onrender.com"
      ).replace(/\/$/, "");
      window.location.replace(`${apiBase}/shopify/auth${window.location.search}`);
      return;
    }
    if (isEmbeddedShopify) {
      getShopifyAppBridge();
    }
  }, [isEmbeddedShopify, hmac]);

  const loading = auth?.loading ?? false;
  const isAuthenticated = auth?.isAuthenticated ?? false;

  const routesWithWhatsApp = ["/sobre", "/login", "/", "/privacy-policy"];
  const showWhatsApp =
    !loading &&
    !isAuthenticated &&
    routesWithWhatsApp.includes(location.pathname);

  // A lista mora em app/rotasPublicas porque o interceptor do axios precisa
  // da mesma noção: lá ela decide se um 401 é sessão expirada ou visitante.
  // Inclui o Admin e a página de status, que têm autenticação própria e não
  // devem ficar presos ao "loading" da sessão do cliente.
  const isPublicRoute = ehRotaPublica(location.pathname);

  if (isEmbeddedShopify && hmac) {
    return <div className="App" />;
  }

  if (loading && !isPublicRoute && !isEmbeddedShopify) {
    return <div style={{ minHeight: "100vh", background: "#f4f7fa" }} />;
  }

  return (
    <div className="App">
      <Routes>
        <Route
          path="/"
          element={
            isEmbeddedShopify ? (
              <ShopifyHome />
            ) : (
              <Navigate
                to={isAuthenticated ? paths.dashboard : "/sobre"}
                replace
              />
            )
          }
        />

        <Route path="/sobre" element={<Sobre />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/shopify-install" element={<ShopifyInstall />} />
        <Route path={paths.nuvemshopConectado} element={<NuvemshopConectado />} />
        <Route path={paths.nuvemshopEmbedded} element={<NuvemshopEmbedded />} />
        <Route path={paths.status} element={<StatusPage />} />
        <Route path={paths.apiDocs} element={<ApiDocsPage />} />

        <Route path={adminPaths.login} element={<AdminLogin />} />
        <Route element={<AdminProtectedRoute />}>
          <Route path={adminPaths.dashboard} element={<AdminDashboard />} />
          <Route path={adminPaths.clientes} element={<AdminClientes />} />
          <Route path={adminPaths.clienteDetalhe} element={<AdminClienteDetalhe />} />
          <Route path={adminPaths.envios} element={<AdminEnvios />} />
          <Route path={adminPaths.integracoes} element={<AdminIntegracoes />} />
          <Route path={adminPaths.comunicacao} element={<AdminComunicacao />} />
          <Route path={adminPaths.changelog} element={<AdminChangelog />} />
          <Route path={adminPaths.roadmap} element={<AdminRoadmap />} />
          <Route
            path={adminPaths.equipe}
            element={<AdminProtectedRoute requiredRoles={["admin"]} />}
          >
            <Route index element={<AdminEquipe />} />
          </Route>
          <Route path={adminPaths.logs} element={<AdminLogs />} />
          <Route path={adminPaths.relatorios} element={<AdminRelatorios />} />
          <Route path={adminPaths.salaOperacoes} element={<AdminSalaOperacoes />} />
          <Route path={adminPaths.maquinaDoTempo} element={<AdminMaquinaDoTempo />} />
          <Route path={adminPaths.chamados} element={<AdminChamados />} />
          <Route path={adminPaths.chamadoDetalhe} element={<AdminChamadoDetalhe />} />
          <Route path={adminPaths.carteira} element={<AdminCarteira />} />
          <Route path={adminPaths.regimes} element={<AdminRegimes />} />
        </Route>

        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to={paths.dashboard} replace />
            ) : (
              <Login />
            )
          }
        />

        <Route
          path="/cadastro"
          element={
            isAuthenticated ? (
              <Navigate to={paths.dashboard} replace />
            ) : (
              <SignUpWizard />
            )
          }
        />

        <Route
          path="/SignUpWizard"
          element={<Navigate to="/cadastro" replace />}
        />

        <Route element={<ProtectedRoute />}>
          <Route
            path={paths.bemVindo}
            element={<Navigate to={paths.dashboard} replace />}
          />
          <Route path={paths.caixas} element={<HomeCaixa />} />
          <Route path={paths.cadastrarCaixa} element={<CadastrarCaixa />} />
          <Route path={paths.editarCaixa} element={<EditarCaixa />} />
          <Route path={paths.cotacao} element={<UpsWizard />} />
          <Route
            path={paths.importPedidos}
            element={<PedidosMinimalUpload />}
          />
          <Route path={paths.nuvemshopConectar} element={<NuvemshopConectar />} />
          <Route path={paths.dashboard} element={<Dashboard />} />
          <Route path={paths.logout} element={<Logout />} />
        </Route>

        <Route
          path="*"
          element={
            <Navigate
              to={isAuthenticated ? paths.dashboard : "/login"}
              replace
            />
          }
        />
      </Routes>

      {showWhatsApp && <WhatsAppButton />}
    </div>
  );
}

export default App;